import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useReadyForMatchCandidatesList, READY_FOR_MATCH_DEFAULT_PAGE_SIZE } from "./useReadyForMatchCandidatesList.js";
import { getReadyForMatchCandidate, markReadyForMatch } from "./readyForMatchApi.js";
import WorkerDetailedProfileDialog from "../workerDetailedProfile/WorkerDetailedProfileDialog.jsx";
import "./readyForMatch.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Intake", "Ready for Match"];
const DRAWER_TITLE_ID = "rfm-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const HERO_COPY_TEXT = "ตรวจแรงงานที่มี Worker Profile แล้ว ก่อนส่งเข้า queue จับคู่งานในอนาคต หน้านี้ยังไม่ใช่การ match งาน";
const MARK_READY_WARNING_TEXT = "การกดปุ่มนี้ยังไม่ใช่การจับคู่งาน และยังไม่สร้าง assignment";
const MARK_READY_BUTTON_TEXT = "Mark Ready For Match / ยืนยันพร้อม Match";
const MARK_READY_SUCCESS_TEXT = "ทำเครื่องหมาย Ready for Match สำเร็จแล้ว — แรงงานคนนี้ออกจากคิว Ready For Match นี้แล้ว";
const PLC_SUPER_AGENT_VIEW_ONLY_TEXT =
  'PLC / Super Agent: มุมมองอ่านอย่างเดียว (view-only) สำหรับหน้านี้ — เห็นรายการและรายละเอียดได้ครบ แต่ปุ่ม "ยืนยันพร้อม Match" จะแสดงเป็น disabled พร้อมข้อความอธิบายสิทธิ์แทนที่จะซ่อนปุ่มไปเลย';
const NOT_ELIGIBLE_NOTE_TEXT =
  "ไม่มีสิทธิ์หรือยังไม่ครบเงื่อนไขสำหรับการทำเครื่องหมาย Ready for Match ในสถานะปัจจุบัน (ต้องเป็น SuperAdmin หรือ Agent เจ้าของสายงาน และผ่านเงื่อนไขความพร้อมทั้งหมดด้านบน)";
const SKILLS_BUDDY_NOT_PRECONDITION_TEXT =
  'ทักษะและ Buddy ยังไม่ใช่เงื่อนไขบังคับของการ Mark Ready for Match ในระบบวันนี้';
const EMPLOYER_WORKSITE_RESERVED_TEXT = "ยังไม่จับคู่ (ไม่ใช่ขอบเขตของ Ready for Match)";
const DOC_NUMBER_RESERVED_TEXT = "endpoint นี้ไม่มีเลขเอกสาร (แม้แบบปิดบัง) และไม่มี storage object key ในข้อมูลตอบกลับ";
const SKILLS_LIST_RESERVED_TEXT = "ดูในรายละเอียด";
const VIEW_ONLY_ROLES = ["Plc", "SuperAgent"];

// Only WorkerStatus/ComplianceStatus value P099 ever writes today - not
// inventing options that don't exist yet.
const WORKER_STATUS_OPTIONS = [{ value: "ProfileCreated", label: "สร้างโปรไฟล์แล้ว" }];
const COMPLIANCE_STATUS_OPTIONS = [{ value: "NotReadyForMatch", label: "รอ Compliance" }];

const WORKER_STATUS_LABELS = { ProfileCreated: "สร้างโปรไฟล์แล้ว" };
const COMPLIANCE_STATUS_LABELS = { NotReadyForMatch: "รอ Compliance" };

function workerStatusLabel(code) {
  return WORKER_STATUS_LABELS[code] ?? code ?? "-";
}

function complianceStatusLabel(code) {
  return COMPLIANCE_STATUS_LABELS[code] ?? code ?? "-";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงคิว Ready For Match นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายการนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Worker Profile นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * P148: the lifecycle status badge. Three distinct states, because they demand
 * three different responses from an operator:
 *  - eligible: nothing to do, this worker can be offered to an employer.
 *  - legacy ready but incomplete: the historical flag really is set, so the
 *    record is not untouched - but the data does not meet the current rule and
 *    they will not be recommended until it does.
 *  - incomplete: never marked ready, data missing.
 */
function ReadinessBadge({ readiness }) {
  if (!readiness) {
    return null;
  }

  const variant =
    readiness.statusCode === "Eligible"
      ? "wx-rfm-ready-badge--ok"
      : readiness.statusCode === "LegacyReadyIncomplete"
        ? "wx-rfm-ready-badge--legacy"
        : "wx-rfm-ready-badge--todo";

  return <span className={`wx-rfm-ready-badge ${variant}`}>{readiness.statusLabel}</span>;
}

/**
 * P148: the per-worker readiness checklist. Blocking items come first and are
 * marked as such, so a missing photo never looks like the reason a worker
 * cannot be matched.
 */
function ReadinessChecklist({ readiness, onOpenProfile }) {
  if (!readiness) {
    return null;
  }

  const blocking = readiness.checklist.filter((item) => item.blocking);
  const quality = readiness.checklist.filter((item) => !item.blocking);
  const canOpenProfile = Boolean(readiness.detailedProfileApplicationId);

  return (
    <section className="wx-rfm-dsec">
      <h3>ความพร้อมสำหรับการจับคู่งาน</h3>
      <ReadinessBadge readiness={readiness} />

      {readiness.missingBlockingLabels.length > 0 && (
        <p className="wx-rfm-ready-missing">
          ยังขาด {readiness.missingBlockingLabels.length} รายการที่จำเป็น: {readiness.missingBlockingLabels.join(", ")}
        </p>
      )}

      <h4 className="wx-rfm-ready-subhead">ข้อมูลที่จำเป็นก่อนจับคู่</h4>
      <ul className="wx-rfm-ready-list">
        {blocking.map((item) => (
          <li key={item.key} className={item.filled ? "wx-rfm-ready-item--ok" : "wx-rfm-ready-item--missing"}>
            <span aria-hidden="true">{item.filled ? "\u2713" : "\u2022"}</span>
            <span>{item.label}</span>
            {item.detail && <small>{item.detail}</small>}
          </li>
        ))}
      </ul>

      <h4 className="wx-rfm-ready-subhead">
        ข้อมูลเสริม (ไม่บล็อกการจับคู่) {readiness.qualityFilledCount}/{readiness.qualityTotalCount}
      </h4>
      <ul className="wx-rfm-ready-list">
        {quality.map((item) => (
          <li key={item.key} className={item.filled ? "wx-rfm-ready-item--ok" : "wx-rfm-ready-item--optional"}>
            <span aria-hidden="true">{item.filled ? "\u2713" : "\u2022"}</span>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>

      {/* A real route to fix what is missing - never a dead end. */}
      {readiness.missingBlockingLabels.length > 0 &&
        (canOpenProfile ? (
          <button type="button" className="wx-button wx-button--primary wx-rfm-fill-profile-button" onClick={onOpenProfile}>
            เติมข้อมูล Worker Profile
          </button>
        ) : (
          <p className="wx-rfm-notice wx-rfm-notice--warn">
            แรงงานคนนี้ไม่ได้มาจากการสมัครผ่านระบบ จึงยังไม่มีแฟ้มข้อมูลละเอียดให้แก้ไข
          </p>
        ))}

      <p className="wx-rfm-ready-gap">{readiness.contractGapNote}</p>
    </section>
  );
}

function describeMarkReadyError(error) {
  if (error?.status === 409) {
    return "สถานะของ Worker Profile นี้เปลี่ยนไปแล้ว (เช่น ถูก Mark Ready for Match ไปแล้ว หรือไม่ผ่านเงื่อนไขอีกต่อไป) กรุณารีเฟรชแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำเครื่องหมาย Ready for Match สำหรับรายการนี้ (เฉพาะ SuperAdmin หรือ Agent เจ้าของสายงานเท่านั้น)";
  }
  return "ไม่สามารถทำเครื่องหมาย Ready for Match ได้ กรุณาลองใหม่อีกครั้ง";
}

/** Two-letter monogram from a full name - never a fabricated photo. */
function monogram(fullName) {
  const trimmed = (fullName ?? "").trim();
  if (!trimmed) {
    return "-";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

// The four identity fields this endpoint's own list item actually carries
// (fullName/phoneNumber/nationality/agent.agentId) - a subset of the eight
// real backend preconditions in ReadyForMatchService.BuildDetailDtoAsync
// (the other two, SourceApplicationId/SourceEkycReviewId, only appear on
// the detail response, not the list item). Used for both the table's
// "Profile completeness" column and the "Missing Identity" KPI tile - never
// claimed as the full/authoritative readiness determination, which is
// always detail.nextActions.canMarkReadyForMatch once a row is opened.
function identityFieldsPresent(item) {
  return [Boolean(item.fullName), Boolean(item.phoneNumber), Boolean(item.nationality), Boolean(item.agent?.agentId)];
}

function profileCompleteness(item) {
  const present = identityFieldsPresent(item);
  const okCount = present.filter(Boolean).length;
  return { okCount, total: present.length, complete: okCount === present.length };
}

/** Client-side-only eligibility preview from the list item's own fields - see identityFieldsPresent above for why this is a preview, not the final answer. */
function readyStatusPreview(item) {
  const { complete } = profileCompleteness(item);
  return complete && item.workerStatus === "ProfileCreated";
}

function uniqueByValue(items, pick) {
  const seen = new Map();
  for (const item of items) {
    const value = pick(item);
    if (value && !seen.has(value)) {
      seen.set(value, value);
    }
  }
  return [...seen.values()];
}

function uniqueAgents(items) {
  const seen = new Map();
  for (const item of items) {
    const agentId = item.agent?.agentId;
    if (agentId && !seen.has(agentId)) {
      seen.set(agentId, item.agent);
    }
  }
  return [...seen.values()];
}

function lineKey(line) {
  if (!line?.superAgentOrganizationId && !line?.plcOrganizationId) {
    return null;
  }
  return `${line.superAgentOrganizationId ?? ""}::${line.plcOrganizationId ?? ""}`;
}

function uniqueLines(items) {
  const seen = new Map();
  for (const item of items) {
    const key = lineKey(item.line);
    if (key && !seen.has(key)) {
      seen.set(key, item.line);
    }
  }
  return [...seen.values()];
}

function readinessChecklist(detail) {
  return [
    { key: "profileCreated", label: "Worker Profile สร้างแล้ว (WorkerStatus = ProfileCreated)", ok: detail.workerStatus === "ProfileCreated" },
    { key: "sourceApplication", label: "มี Source Application อ้างอิง", ok: Boolean(detail.sourceApplicationId) },
    { key: "sourceEkyc", label: "มี Source e-KYC Review อ้างอิง", ok: Boolean(detail.sourceEkycReviewId) },
    { key: "fullName", label: "มีชื่อแรงงาน", ok: Boolean(detail.fullName) },
    { key: "phone", label: "มีเบอร์โทร", ok: Boolean(detail.phoneNumber) },
    { key: "nationality", label: "มีสัญชาติ", ok: Boolean(detail.nationality) },
    { key: "agent", label: "มี Agent เจ้าของสายงาน", ok: Boolean(detail.agent?.agentId) },
    { key: "notAlreadyReady", label: "ยังไม่ถูก Mark Ready for Match มาก่อน", ok: detail.readyForMatch !== true },
  ];
}

function markReadyPermissionNote(primaryRole, nextActions) {
  if (VIEW_ONLY_ROLES.includes(primaryRole)) {
    return PLC_SUPER_AGENT_VIEW_ONLY_TEXT;
  }
  if (nextActions?.canMarkReadyForMatch) {
    return `สิทธิ์ปัจจุบัน: ${primaryRole ?? "-"} — กด "ยืนยันพร้อม Match" ได้ (SuperAdmin หรือ Agent เจ้าของสายงานเท่านั้นที่กดได้)`;
  }
  return NOT_ELIGIBLE_NOTE_TEXT;
}

/**
 * /backoffice/worker-intake/ready-for-match - the Ready For Match queue
 * (Prompt 100B, built from the W47 reference). SuperAdmin (and, by scope,
 * the owning Agent) backoffice screen for reviewing Worker Profiles
 * (Prompt 097) that are not yet ReadyForMatch, and marking one ready over
 * the Prompt 099 API: GET /api/backoffice/ready-for-match/candidates (list),
 * GET /api/backoffice/ready-for-match/candidates/{workerId} (detail), and
 * POST /api/backoffice/worker-profiles/{workerId}/ready-for-match (the one
 * write action).
 *
 * Ready For Match is a readiness-flag update only - this file never calls
 * the Worker Profile create endpoint, the e-KYC decision endpoint, or any
 * matching/assignment/tracking/buddy/invite-card API (none of the last four
 * exist anywhere in this codebase). "Create Assignment" and "Start
 * Tracking" always render disabled with no onClick at all, regardless of
 * what any nextActions value says, because neither exists yet.
 *
 * Filtering/pagination is real server-side (mirrors useWorkerProfilesList.js
 * / useEkycReviewsList.js), and only ever forwards the P099-whitelisted
 * query params (see readyForMatchApi.js) - never an unfiltered pass-through
 * of whatever the UI happens to hold in state. Nationality/Language/Agent/
 * Line filter options are derived client-side from the currently loaded
 * page's own real item fields (this endpoint's summary DTO has no
 * byNationality/byAgent breakdown, unlike the sibling e-KYC/Worker Profile
 * APIs) - a real, disclosed page-scoped limitation, never a fabricated
 * option list.
 */
export default function ReadyForMatchPage() {
  const { data: identity } = useBackofficeIdentity();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [workerStatusFilter, setWorkerStatusFilter] = useState("");
  const [complianceStatusFilter, setComplianceStatusFilter] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [page, setPage] = useState(1);
  // Clicking the row opens the ONE shared worker record; the row's own
  // action button keeps opening this screen's process detail.
  const [workerRecordId, setWorkerRecordId] = useState(null);

  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [activeTab, setActiveTab] = useState("identity");
  const [markReadySubmit, setMarkReadySubmit] = useState({ status: "idle", error: null });

  // Debounce free-text search before it ever becomes part of the fetch
  // filters - typing does not fire one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any real filter change (not a page change) returns to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, workerStatusFilter, complianceStatusFilter, nationalityFilter, languageFilter, agentFilter, lineFilter]);

  // Opening a different row (or closing the drawer) always starts fresh -
  // the mark-ready submit state must never leak from one worker to another.
  useEffect(() => {
    setActiveTab("identity");
    setMarkReadySubmit({ status: "idle", error: null });
  }, [selectedWorkerId]);

  const [lineSuperAgentOrganizationId, linePlcOrganizationId] = lineFilter ? lineFilter.split("::") : ["", ""];

  const filters = useMemo(
    () => ({
      page,
      pageSize: READY_FOR_MATCH_DEFAULT_PAGE_SIZE,
      search,
      workerStatus: workerStatusFilter,
      complianceStatus: complianceStatusFilter,
      nationality: nationalityFilter,
      preferredLanguageCode: languageFilter,
      agentId: agentFilter,
      superAgentOrganizationId: lineSuperAgentOrganizationId,
      plcOrganizationId: linePlcOrganizationId,
    }),
    [page, search, workerStatusFilter, complianceStatusFilter, nationalityFilter, languageFilter, agentFilter, lineSuperAgentOrganizationId, linePlcOrganizationId]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useReadyForMatchCandidatesList(filters);

  // Detail is fetched only when a row is opened, and only refetched for the
  // same open row when Refresh is pressed or a mark-ready action succeeds
  // (detailReloadToken) - never on an interval, never speculatively, and
  // never without a real workerId.
  useEffect(() => {
    if (!selectedWorkerId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedWorkerId ? previous.data : null,
      error: null,
      forId: selectedWorkerId,
    }));

    getReadyForMatchCandidate(selectedWorkerId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedWorkerId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedWorkerId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedWorkerId
  }, [selectedWorkerId, detailReloadToken]);

  function openDrawer(workerId) {
    setSelectedWorkerId(workerId);
  }

  function closeDrawer() {
    setSelectedWorkerId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedWorkerId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  // P148: re-reads the open candidate after the detailed profile is saved,
  // so the readiness checklist and the mark-ready button reflect the new
  // data immediately instead of waiting for someone to reload the page.
  function handleProfileSaved() {
    setDetailReloadToken((token) => token + 1);
  }

  async function handleMarkReady() {
    if (!selectedWorkerId) {
      return;
    }
    setMarkReadySubmit({ status: "submitting", error: null });
    try {
      await markReadyForMatch(selectedWorkerId);
      setMarkReadySubmit({ status: "success", error: null });
      // A successful mark-ready changes both this row's readyForMatch/
      // nextActions and the KPI summary, and removes the row from this
      // NotReadyForMatch-only queue entirely - refresh both instead of only
      // patching local state, so the table/KPI strip and the drawer always
      // reflect what the backend actually persisted (the drawer stays open
      // to show the success state; the table loses the row on its own once
      // refetchList resolves).
      refetchList();
      setDetailReloadToken((token) => token + 1);
    } catch (error) {
      setMarkReadySubmit({ status: "error", error });
    }
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / READY_FOR_MATCH_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search || workerStatusFilter || complianceStatusFilter || nationalityFilter || languageFilter || agentFilter || lineFilter
  );

  const nationalityOptions = useMemo(() => uniqueByValue(items, (item) => item.nationality), [items]);
  const languageOptions = useMemo(() => uniqueByValue(items, (item) => item.preferredLanguageCode), [items]);
  const agentOptions = useMemo(() => uniqueAgents(items), [items]);
  const lineOptions = useMemo(() => uniqueLines(items), [items]);

  // Derived-only, page-scoped (see identityFieldsPresent comment above) -
  // never claimed as a platform-wide total, since summary.missingSkillsCount
  // /missingBuddyCount are the only two summary fields P099 leaves
  // deliberately null (real backend reason: no cheap way to compute them),
  // and there is no missingIdentityCount summary field at all to be honest
  // about instead.
  const missingIdentityOnPage = items.filter((item) => !profileCompleteness(item).complete).length;

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดคิว Ready For Match..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดคิว Ready For Match ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-rfm-page">
        <div className="wx-rfm-toolbar">
          <label className="wx-rfm-search">
            <span className="wx-visually-hidden">ค้นหาชื่อแรงงาน เบอร์โทร หรือรหัส Worker</span>
            <input
              type="search"
              placeholder="ค้นหาชื่อแรงงาน เบอร์โทร หรือรหัส Worker"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-rfm-filters">
            <label className="wx-rfm-filter">
              <span>Worker Status</span>
              <select value={workerStatusFilter} onChange={(event) => setWorkerStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {WORKER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-rfm-filter">
              <span>Compliance</span>
              <select value={complianceStatusFilter} onChange={(event) => setComplianceStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {COMPLIANCE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-rfm-filter">
              <span>สัญชาติ</span>
              <select value={nationalityFilter} onChange={(event) => setNationalityFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {nationalityOptions.map((nationality) => (
                  <option key={nationality} value={nationality}>
                    {nationality}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-rfm-filter">
              <span>ภาษา</span>
              <select value={languageFilter} onChange={(event) => setLanguageFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {languageOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-rfm-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {agentOptions.map((agent) => (
                  <option key={agent.agentId} value={agent.agentId}>
                    {agent.agentCode} · {agent.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-rfm-filter">
              <span>สายงาน</span>
              <select value={lineFilter} onChange={(event) => setLineFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {lineOptions.map((line) => (
                  <option key={lineKey(line)} value={lineKey(line)}>
                    {line.superAgentName} · {line.plcName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="wx-rfm-toolbar-actions">
            {/* wx-rfm-refresh-button is also listed in DetailDrawer's
                selectableSelector below - without it, clicking Refresh
                while a row is open is an "outside click" as far as the
                drawer's own close-on-outside-click listener is concerned. */}
            <button type="button" className="wx-button wx-button--ghost wx-rfm-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
          </div>
        </div>

        <header className="wx-rfm-head">
          <div className="wx-rfm-title">
            <h1>Ready For Match</h1>
            <p>{HERO_COPY_TEXT}</p>
          </div>
          {identity?.primaryRole && (
            <span className="wx-rfm-scope-pill">
              Scope <b>{identity.primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-rfm-metrics">
          <MetricCard label="Candidates" value={summary?.candidateCount} note="Worker Profile ที่ยังไม่ Ready for Match" />
          <MetricCard label="Ready For Match" value={summary?.readyForMatchCount} note="Mark แล้ว ทั้งแพลตฟอร์ม" variant="green" />
          <MetricCard label="Not Ready" value={summary?.notReadyForMatchCount} note="เท่ากับ Candidates เสมอ ตามนิยาม queue นี้" variant="amber" />
          <MetricCard
            label="Missing Identity"
            value={missingIdentityOnPage}
            note="จากรายการหน้านี้ (ไม่ใช่ยอดรวมทั้งระบบ)"
            variant="derived"
          />
          <MetricCard label="Missing Skills" reserved={summary?.missingSkillsCount == null} value={summary?.missingSkillsCount} />
          <MetricCard label="Missing Buddy" reserved={summary?.missingBuddyCount == null} value={summary?.missingBuddyCount} />
        </div>

        <section className="wx-panel wx-rfm-list-panel">
          <div className="wx-rfm-list-head">
            <h2 className="wx-panel-title">คิว Ready For Match</h2>
            <span className="wx-rfm-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-rfm-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">
              {hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ไม่มีแรงงานที่รอ Ready for Match ในขณะนี้"}
            </p>
          ) : (
            <>
              <div className="wx-rfm-table-wrap">
                <table className="wx-rfm-table">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "10%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Worker</th>
                      <th>Contact</th>
                      <th>Agent / Line</th>
                      <th>Profile completeness</th>
                      <th>Skills</th>
                      <th>Buddy</th>
                      <th>Compliance</th>
                      <th>Ready Status</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const completeness = profileCompleteness(item);
                      const eligible = readyStatusPreview(item);
                      return (
                        <tr key={item.workerId} className="wx-rfm-row" onClick={() => setWorkerRecordId(item.workerId)}>
                          <td>
                            <div className="wx-rfm-worker-cell">
                              <span className="wx-rfm-avatar" aria-hidden="true">
                                {monogram(item.fullName)}
                              </span>
                              <div>
                                <span className="wx-rfm-cell-strong">{item.fullName || "-"}</span>
                                <span className="wx-rfm-cell-sub">{item.workerCode}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="wx-rfm-cell-strong">{item.phoneNumber ?? "-"}</span>
                            <span className="wx-rfm-cell-sub">ภาษา: {item.preferredLanguageCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-rfm-cell-strong">
                              {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
                            </span>
                            <span className="wx-rfm-cell-sub">
                              {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
                            </span>
                          </td>
                          <td>
                            <span className={`wx-badge ${completeness.complete ? "wx-badge--success" : "wx-badge--danger"}`}>
                              {completeness.okCount}/{completeness.total} {completeness.complete ? "ครบ" : `ขาด ${completeness.total - completeness.okCount}`}
                            </span>
                          </td>
                          <td>
                            {/* skills[] is not on the list item DTO - never fabricate a sample chip here. */}
                            <span className="wx-rfm-cell-sub">{SKILLS_LIST_RESERVED_TEXT}</span>
                          </td>
                          {/* A "Buddy" cell reading "สงวนไว้" on every single
                              row was here - no buddy field exists on this API,
                              so the column carried one fixed word per worker. */}
                          <td>
                            <span className="wx-badge wx-badge--warning">{complianceStatusLabel(item.complianceStatus)}</span>
                          </td>
                          <td>
                            <span className={`wx-badge ${eligible ? "wx-badge--success" : "wx-badge--warning"}`}>
                              {eligible ? "พร้อม Mark" : "ยังไม่ครบเงื่อนไข"}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="wx-rfm-row-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDrawer(item.workerId);
                              }}
                            >
                              เปิดรายละเอียด
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="wx-rfm-cards">
                {items.map((item) => (
                  <CandidateCard key={item.workerId} item={item} onOpen={() => openDrawer(item.workerId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={selectedWorkerId !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-rfm-row, .wx-rfm-card, .wx-rfm-refresh-button"
      >
        <CandidateDrawerContent
          state={detailState}
          titleId={DRAWER_TITLE_ID}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          primaryRole={identity?.primaryRole}
          markReadySubmit={markReadySubmit}
          onMarkReady={handleMarkReady}
          onProfileSaved={handleProfileSaved}
        />
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-rfm-row, .wx-rfm-card"
      />
    </AppShell>
  );
}

function MetricCard({ label, value, note, variant, reserved }) {
  // `reserved` here means "the summary did not include this count", which is a
  // real and temporary state - not a feature we never built. It shows a dash,
  // the same as any other absent value, and no pill naming an endpoint.
  if (reserved) {
    return (
      <div className="wx-rfm-metric wx-rfm-metric--reserved">
        <span className="wx-rfm-metric-label">{label}</span>
        <strong className="wx-rfm-metric-value wx-rfm-metric-value--reserved">-</strong>
      </div>
    );
  }
  const variantClass = variant ? ` wx-rfm-metric--${variant}` : "";
  return (
    <div className={`wx-rfm-metric${variantClass}`}>
      <span className="wx-rfm-metric-label">{label}</span>
      <strong className="wx-rfm-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-rfm-metric-note">{note}</span>}
    </div>
  );
}

function CandidateCard({ item, onOpen }) {
  const completeness = profileCompleteness(item);
  const eligible = readyStatusPreview(item);
  return (
    <div className="wx-rfm-card" onClick={onOpen}>
      <div className="wx-rfm-card-top">
        <div className="wx-rfm-worker-cell">
          <span className="wx-rfm-avatar" aria-hidden="true">
            {monogram(item.fullName)}
          </span>
          <div>
            <strong>{item.fullName || "-"}</strong>
            <span>{item.workerCode}</span>
          </div>
        </div>
        <span className={`wx-badge ${eligible ? "wx-badge--success" : "wx-badge--warning"}`}>{eligible ? "พร้อม Mark" : "ยังไม่ครบเงื่อนไข"}</span>
      </div>
      <div className="wx-rfm-card-grid">
        <div>
          <label>เบอร์โทร</label>
          <strong>{item.phoneNumber ?? "-"}</strong>
        </div>
        <div>
          <label>Agent / สายงาน</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.line?.superAgentName ?? "-"}
          </strong>
        </div>
        <div>
          <label>Profile completeness</label>
          <strong>
            {completeness.okCount}/{completeness.total} {completeness.complete ? "ครบ" : `ขาด ${completeness.total - completeness.okCount}`}
          </strong>
        </div>
        <div>
          <label>Compliance</label>
          <strong>{complianceStatusLabel(item.complianceStatus)}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-rfm-row-action wx-rfm-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

function CandidateDrawerContent({ state, titleId, activeTab, onTabChange, primaryRole, markReadySubmit, onMarkReady, onProfileSaved }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-rfm-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;

  return (
    <div className="wx-rfm-drawer">
      {/* One shared record-drawer header for the whole app: the worker's own
          name first, code under it (components/RecordDrawerHeader.jsx). */}
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.worker?.fullNameNative || detail.worker?.fullNameEnglish || detail.workerCode}
        title={detail.worker?.fullNameNative || detail.worker?.fullNameEnglish || detail.workerCode}
        subtitle={`${detail.workerCode} · Worker Profile สร้างแล้ว`}
        badges={[
          {
            label: detail.readyForMatch ? "Ready for Match แล้ว" : "ยังไม่ Ready for Match",
            variant: detail.readyForMatch ? "success" : "warning",
          },
          { label: "Worker Profile สร้างแล้ว", variant: "info" },
          { label: "ยังไม่จับคู่งาน / ยังไม่มี assignment", variant: "danger" },
        ]}
        tabs={[
          { key: "identity", label: "ข้อมูลแรงงาน" },
          { key: "skills", label: "ทักษะ/ประวัติ/เอกสาร" },
          { key: "decision", label: "ความพร้อม Match" },
        ]}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      {activeTab === "identity" && <IdentityPane detail={detail} />}
      {activeTab === "skills" && <SkillsHistoryDocumentsPane detail={detail} />}
      {activeTab === "decision" && (
        <ReadinessDecisionPane
          detail={detail}
          primaryRole={primaryRole}
          markReadySubmit={markReadySubmit}
          onMarkReady={onMarkReady}
          onProfileSaved={onProfileSaved}
        />
      )}
    </div>
  );
}

function IdentityPane({ detail }) {
  const contacts = detail.contacts ?? [];
  return (
    <>
      <section className="wx-rfm-dsec wx-rfm-dsec-row">
        <div className="wx-rfm-dsec-col">
          <h3>ข้อมูลแรงงาน</h3>
          <div className="wx-rfm-identity-block">
            <span className="wx-rfm-avatar wx-rfm-avatar--lg" aria-hidden="true">
              {monogram(detail.fullName)}
            </span>
            <div className="wx-rfm-identity-block-text">
              <strong>{detail.fullName || "-"}</strong>
              <span>{detail.workerCode}</span>
            </div>
          </div>
          <div className="wx-rfm-kv-row">
            <span>สัญชาติ</span>
            <strong>{detail.nationality ?? "-"}</strong>
          </div>
          <div className="wx-rfm-kv-row">
            <span>ภาษา</span>
            <strong>{detail.preferredLanguageCode ?? "-"}</strong>
          </div>
          <div className="wx-rfm-kv-row">
            <span>เบอร์โทร</span>
            <strong>{detail.phoneNumber ?? "-"}</strong>
          </div>
          <div className="wx-rfm-kv-row">
            <span>Worker Status</span>
            <strong>{workerStatusLabel(detail.workerStatus)}</strong>
          </div>
        </div>
        <div className="wx-rfm-dsec-col">
          <h3>Agent และสายงาน</h3>
          <div className="wx-rfm-kv-row">
            <span>Agent</span>
            <strong>
              {detail.agent?.agentCode ?? "-"} · {detail.agent?.displayName ?? "-"}
            </strong>
          </div>
          <div className="wx-rfm-kv-row">
            <span>Super Agent</span>
            <strong>{detail.line?.superAgentName ?? "-"}</strong>
          </div>
          <div className="wx-rfm-kv-row">
            <span>PLC / บมจ</span>
            <strong>{detail.line?.plcName ?? "-"}</strong>
          </div>
          {/* An "เบอร์ติดต่อ Agent" row printed "ไม่มีในข้อมูลตอบกลับ API นี้
              (สงวนไว้)" as its value. The number genuinely is not on this
              contract, so the row goes rather than the excuse staying. */}
          <div className="wx-rfm-kv-row">
            <span>นายจ้าง / ไซต์งาน</span>
            {/* Ready For Match happens before any employer/job assignment - always reserved. */}
            <strong className="wx-rfm-reserved-text">{EMPLOYER_WORKSITE_RESERVED_TEXT}</strong>
          </div>
        </div>
      </section>

      <section className="wx-rfm-dsec">
        <h3>ช่องทางติดต่อ (Contacts)</h3>
        {contacts.length === 0 ? (
          <p className="wx-state-body">ยังไม่มีข้อมูลช่องทางติดต่อ</p>
        ) : (
          <div className="wx-rfm-doc-list">
            {contacts.map((contact, index) => (
              <div className="wx-rfm-doc-item" key={`${contact.contactType}-${index}`}>
                <div className="wx-rfm-doc-item-name">
                  <strong>{contact.contactType ?? "-"}</strong>
                  <span>{contact.isPrimary ? "ช่องทางหลัก (Primary)" : "ช่องทางรอง"}</span>
                </div>
                <span className="wx-rfm-doc-value">{contact.contactValue ?? "-"}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function SkillsHistoryDocumentsPane({ detail }) {
  const skills = detail.skills ?? [];
  const workHistory = detail.workHistory ?? [];
  const documents = detail.documents ?? [];
  return (
    <>
      <section className="wx-rfm-dsec">
        <h3>ทักษะ</h3>
        {skills.length === 0 ? (
          <p className="wx-state-body">ยังไม่มีข้อมูลทักษะ</p>
        ) : (
          <div className="wx-rfm-skill-chips">
            {skills.map((skill, index) => (
              <span className="wx-rfm-skill-chip" key={`${skill.skillCode}-${index}`}>
                {skill.skillName ?? skill.skillCode}
                {skill.proficiencyLevel && <small>{skill.proficiencyLevel}</small>}
              </span>
            ))}
          </div>
        )}
      </section>

      <section className="wx-rfm-dsec">
        <h3>ประวัติการทำงาน</h3>
        {workHistory.length === 0 ? (
          <p className="wx-state-body">ยังไม่มีประวัติงาน</p>
        ) : (
          <div className="wx-rfm-history-list">
            {workHistory.map((entry, index) => (
              <div className="wx-rfm-history-item" key={`${entry.employerNameText}-${index}`}>
                <div className="wx-rfm-history-item-top">
                  <strong>{entry.employerNameText ?? "-"}</strong>
                  <span>
                    {formatDateTimeTh(entry.startedAtUtc) ?? "-"} – {formatDateTimeTh(entry.endedAtUtc) ?? "ปัจจุบัน"}
                  </span>
                </div>
                <p>{entry.roleTitle ?? "-"}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="wx-rfm-dsec">
        <h3>เอกสาร</h3>
        {documents.length === 0 ? (
          <p className="wx-state-body">ยังไม่มีเอกสารสำหรับรายการนี้</p>
        ) : (
          <div className="wx-rfm-doc-list">
            {documents.map((doc, index) => (
              <div className="wx-rfm-doc-item" key={`${doc.documentType}-${index}`}>
                <div className="wx-rfm-doc-item-name">
                  <strong>{doc.documentType ?? "-"}</strong>
                  <span>{doc.documentStatus ?? "-"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* documentType/documentStatus only - this DTO has no masked
            document number field and no storage object key field at all
            (not even one to mask). Never invent either. */}
        <p className="wx-rfm-note-muted wx-rfm-note-muted--left">{DOC_NUMBER_RESERVED_TEXT}</p>
      </section>
    </>
  );
}

function ReadinessDecisionPane({ detail, primaryRole, markReadySubmit, onMarkReady, onProfileSaved }) {
  const nextActions = detail.nextActions ?? {};
  // P148: the lifecycle verdict, computed by the backend on every read.
  const readiness = detail.readiness ?? null;
  const checklist = readinessChecklist(detail);
  const submitting = markReadySubmit.status === "submitting";
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  // Belt-and-braces beyond the backend's own nextActions.canMarkReadyForMatch
  // (which the real ReadyForMatchService already computes as false for a
  // PLC/SuperAgent actor - see markReadyPermissionNote above): never enable
  // this button for a view-only role client-side either, so a
  // misidentified/stale primaryRole can never silently enable an action the
  // actor should not have.
  const canMark = nextActions.canMarkReadyForMatch === true && !submitting && !VIEW_ONLY_ROLES.includes(primaryRole);

  return (
    <>
      <section className="wx-rfm-dsec">
        <h3>Readiness checklist</h3>
        <p className="wx-rfm-dsec-sub">เงื่อนไขที่ระบบตรวจก่อนอนุญาตให้ Mark Ready for Match</p>
        <ul className="wx-rfm-checklist">
          {checklist.map((entry) => (
            <li key={entry.key} className={entry.ok ? "wx-rfm-checklist-ok" : "wx-rfm-checklist-pending"}>
              <span className="wx-rfm-check-dot" aria-hidden="true" />
              {entry.label}
            </li>
          ))}
        </ul>
        <p className="wx-rfm-note-muted wx-rfm-note-muted--left">{SKILLS_BUDDY_NOT_PRECONDITION_TEXT}</p>
      </section>

      <section className="wx-rfm-dsec">
        <h3>สิทธิ์การกดปุ่มนี้</h3>
        <div className={`wx-rfm-notice${VIEW_ONLY_ROLES.includes(primaryRole) ? " wx-rfm-notice--warn" : ""}`}>
          {markReadyPermissionNote(primaryRole, nextActions)}
        </div>
      </section>

      <ReadinessChecklist readiness={readiness} onOpenProfile={() => setProfileDialogOpen(true)} />

      {readiness?.detailedProfileApplicationId && (
        <WorkerDetailedProfileDialog
          open={profileDialogOpen}
          applicationId={readiness.detailedProfileApplicationId}
          onClose={() => setProfileDialogOpen(false)}
          onSaved={onProfileSaved}
        />
      )}

      <section className="wx-rfm-dsec">
        <h3>การตัดสินใจ — Mark Ready For Match</h3>
        <button type="button" className="wx-button wx-button--primary wx-rfm-mark-ready-button" disabled={!canMark} onClick={onMarkReady}>
          {submitting ? "กำลังบันทึก..." : MARK_READY_BUTTON_TEXT}
        </button>
        <div className="wx-rfm-notice wx-rfm-notice--warn">{MARK_READY_WARNING_TEXT}</div>

        {markReadySubmit.status === "error" && (
          <p className="wx-rfm-form-message" role="alert">
            {describeMarkReadyError(markReadySubmit.error)}
          </p>
        )}
        {markReadySubmit.status === "success" && (
          <p className="wx-rfm-form-success" role="status">
            {MARK_READY_SUCCESS_TEXT}
          </p>
        )}
      </section>

        {/* A "ขั้นตอนถัดไป (สงวนไว้)" section stood here: a heading plus
            two or three buttons that were disabled unconditionally, with
            sub-labels explaining which packet had not shipped. Two problems -
            it was a dead end on every visit, and the copy had gone stale (it
            still said WorkerAssignment did not exist, which it now does). A
            real next step belongs here when there is one to offer. */}
    </>
  );
}
